/**
 * @typedef {import('antd').ButtonProps} ButtonProps
 */

/**
 * @typedef {Object} CustomButtonProps
 * @property {object} [eventData]
 * @property {string}  eventData.name To identify the event name
 * @property {string}  [eventData.value] To identify the event value
 * @property {string}  [eventData.data] This will be mapped to `tag` and send as additional data in event payload
 * @property {'default' | 'primary' | 'secondary' | 'tertiary' | 'link' | 'text'}  [variant = 'default'] The variant to use. ['default', 'primary', 'secondary', 'tertiary', 'link', 'text']
 * @property {Boolean}  [inactive = false] The variant to use. ['default', 'primary', 'secondary', 'tertiary', 'link', 'text']
 * @property {Boolean}  [fullWidth = false] In case you need fullWidth button
 */

import React, { MouseEventHandler, MouseEvent } from 'react';

import cx from 'classnames';
import { Button as ButtonCore } from 'antd';
import { isEmpty } from 'lodash';
import { Link } from 'react-router-dom';

import { sendClickEvent } from '@/utils/AnalyticsUtils';
import { isExternalUrl, deleteMultipleKeysInObject } from '@/utils/helper';

import styles from './Button.module.scss';

/**
 * @description Generic component to display Button according to the theme variants.
 *
 * @author  Pooja Waradkar <@pooja.waradkar>
 *
 * @reference https://ant.design/components/button
 *
 * @param {ButtonProps & CustomButtonProps}  [props] https://ant.design/components/button
 *
 * @returns {React.ReactElement}
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Button = (props: any) => {
	const {
		className = '',
		onClick,
		type = 'button',
		children,
		href = '',
		variant = 'default',
		eventData = {},
		inactive = false,
		fullWidth = false,
		...restProps
	} = props;

	const onButtonClickHandler: MouseEventHandler<HTMLAnchorElement> = (
		e: MouseEvent<HTMLAnchorElement>,
	) => {
		if (!isEmpty(eventData)) {
			sendClickEvent(eventData);
		}

		if (typeof onClick === 'function') {
			onClick(e);
		}
	};

	const getLinkButtonProps = () =>
		deleteMultipleKeysInObject({ ...props }, [
			'type',
			'variant',
			'eventData',
			'inactive',
		]);

	const getButtonComponent = () => {
		if (isExternalUrl(href) || !href) {
			return (
				<ButtonCore
					{...restProps}
					type={variant}
					htmlType={type}
					block={fullWidth}
					{...getLinkButtonProps()}
					className={cx(className, styles[variant as keyof typeof styles], {
						[styles.inactive]: inactive,
					})}
					onClick={onButtonClickHandler}
				>
					{children}
				</ButtonCore>
			);
		}
		return (
			<Link
				to={href}
				onClick={onButtonClickHandler}
				{...getLinkButtonProps()}
			/>
		);
	};

	return getButtonComponent();
};

export default Button;
